import { useEffect, useRef } from 'react'

import { useIsTouch } from '@hooks'
import { GamePhase, useGameStore } from '@stores'

const MIN_SWIPE = 50

function emulateKey(code: string, type: 'keydown' | 'keyup') {
  window.dispatchEvent(new KeyboardEvent(type, { code }))
}

export function TouchControls() {
  const isTouch = useIsTouch()

  const phase = useGameStore(state => state.phase)
  const level = useGameStore(state => state.level)

  const locoDirRef = useRef(useGameStore.getState().network.locomotive?.direction ?? 1)
  const activeKeyRef = useRef<string | null>(null)
  const startXRef = useRef(0)

  useEffect(() => {
    locoDirRef.current = useGameStore.getState().network.locomotive?.direction ?? 1
  }, [level])

  useEffect(() => {
    if (!isTouch || phase < GamePhase.PLAYING) return

    function release() {
      if (activeKeyRef.current) {
        emulateKey(activeKeyRef.current, 'keyup')
        activeKeyRef.current = null
      }
    }

    function onTouchStart(e: TouchEvent) {
      startXRef.current = e.touches[0].clientX
    }

    function onTouchMove(e: TouchEvent) {
      const deltaX = e.touches[0].clientX - startXRef.current

      let targetKey: string | null = null
      if (deltaX > MIN_SWIPE) {
        targetKey = locoDirRef.current === 1 ? 'KeyW' : 'KeyS'
      } else if (deltaX < -MIN_SWIPE) {
        targetKey = locoDirRef.current === 1 ? 'KeyS' : 'KeyW'
      }

      if (targetKey !== activeKeyRef.current) {
        release()
        if (targetKey) {
          activeKeyRef.current = targetKey
          emulateKey(targetKey, 'keydown')
        }
      }
    }

    window.addEventListener('touchstart', onTouchStart)
    window.addEventListener('touchmove', onTouchMove)
    window.addEventListener('touchend', release)
    window.addEventListener('touchcancel', release)

    return () => {
      release()
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', release)
      window.removeEventListener('touchcancel', release)
    }
  }, [isTouch, phase])

  return null
}
